import { useState, useCallback, useEffect } from 'react';
import {
    Drawer,
    Box,
    IconButton,
    Typography,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAccount } from '../../context/AccountContext';
import { useImportJobsSSE } from '../../hooks/useImportJobsSSE';
import { ImportJobsList } from './ImportJobsList';
import { ImportJob } from './types';

interface ImportJobsPanelProps {
    open: boolean;
    onClose: () => void;
    policySetMap?: Record<string, string>;
}

function ImportJobsPanel({ open, onClose, policySetMap }: ImportJobsPanelProps) {
    const { selectedAccount, selectedAccountConnection } = useAccount();
    const [jobs, setJobs] = useState<ImportJob[]>([]);
    const [loading, setLoading] = useState(false);
    const [panelWidth, setPanelWidth] = useState(600);
    const [isResizing, setIsResizing] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Get account connection ID from context
    const accountConnectionId = selectedAccountConnection?.id || null;

    // Initial load of jobs
    useEffect(() => {
        const fetchJobs = async () => {
            if (!selectedAccount) {
                setJobs([]);
                return;
            }

            setLoading(true);
            try {
                const response = await fetch('/api/import-jobs/list', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ accountName: selectedAccount, limit: 50 })
                });

                if (response.ok) {
                    const data = await response.json();
                    setJobs(data);
                }
            } catch (error) {
                console.error('Failed to fetch jobs:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load jobs',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            fetchJobs();
        }
    }, [selectedAccount, open]);

    // Handle real-time job updates via SSE
    const handleJobUpdate = useCallback((updatedJob: ImportJob) => {
        setJobs(prevJobs => {
            const existingIndex = prevJobs.findIndex(j => j.jobId === updatedJob.jobId);

            if (existingIndex >= 0) {
                // Update existing job
                const newJobs = [...prevJobs];
                newJobs[existingIndex] = updatedJob;
                return newJobs;
            } else {
                // Add new job at the beginning
                return [updatedJob, ...prevJobs];
            }
        });
    }, []);

    const handlePercentUpdate = useCallback((jobId: string, percent: number) => {
        setJobs(prevJobs => {
            const existingIndex = prevJobs.findIndex(j => j.jobId === jobId);

            if (existingIndex >= 0) {
                // Update only the progressPercentage field
                const newJobs = [...prevJobs];
                newJobs[existingIndex] = {
                    ...newJobs[existingIndex],
                    progressPercentage: percent
                };
                return newJobs;
            }
            return prevJobs;
        });
    }, []);

    const handleSSEError = useCallback((error: Error) => {
        console.error('SSE error:', error);
        // Don't show snackbar for SSE errors - they auto-reconnect
        // and showing the snackbar causes page jumps during initial connection
    }, []);

    // Use SSE for real-time updates
    const { isConnected } = useImportJobsSSE({
        accountConnectionId,
        enabled: open,
        onJobUpdate: handleJobUpdate,
        onPercentUpdate: handlePercentUpdate,
        onError: handleSSEError
    });

    const handleCancelJob = useCallback(async (jobId: string) => {
        if (!selectedAccount) return;

        const confirmed = window.confirm('Are you sure you want to cancel this job?');
        if (!confirmed) return;

        try {
            const response = await fetch(
                `/api/import-jobs/${jobId}?accountName=${encodeURIComponent(selectedAccount)}`,
                { method: 'DELETE' }
            );

            if (!response.ok) {
                throw new Error('Failed to cancel job');
            }

            // Remove the cancelled job from the list immediately
            setJobs(prevJobs => prevJobs.filter(job => job.jobId !== jobId));

            setSnackbar({
                open: true,
                message: 'Job cancelled successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error cancelling job:', error);
            setSnackbar({
                open: true,
                message: `Failed to cancel job: ${(error as Error).message}`,
                severity: 'error'
            });
        }
    }, [selectedAccount]);

    const handleRefresh = useCallback(async () => {
        if (!selectedAccount) return;

        setLoading(true);
        try {
            const response = await fetch('/api/import-jobs/list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accountName: selectedAccount, limit: 50 })
            });

            if (response.ok) {
                const data = await response.json();
                setJobs(data);
            }
        } catch (error) {
            console.error('Failed to refresh jobs:', error);
            setSnackbar({
                open: true,
                message: 'Failed to refresh jobs',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [selectedAccount]);

    const handleCloseSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // Handle panel resizing
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing) return;

        const newWidth = window.innerWidth - e.clientX;
        const minWidth = 400;
        const maxWidth = window.innerWidth * 0.9;

        if (newWidth >= minWidth && newWidth <= maxWidth) {
            setPanelWidth(newWidth);
        }
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    // Add/remove mouse event listeners for resizing
    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: `${panelWidth}px`,
                        maxWidth: '90vw'
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'row', position: 'relative' }}>
                    {/* Resize handle */}
                    <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                            width: '5px',
                            cursor: 'ew-resize',
                            backgroundColor: isResizing ? '#2196f3' : 'transparent',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                                backgroundColor: '#2196f3'
                            },
                            flexShrink: 0,
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: '-3px',
                                right: '-3px',
                                top: 0,
                                bottom: 0
                            }
                        }}
                    />
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">Publication Jobs</Typography>
                            {isConnected && (
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: '#4caf50',
                                        boxShadow: '0 0 4px rgba(76, 175, 80, 0.6)'
                                    }}
                                    title="Connected to live updates"
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                disabled={loading}
                                title="Refresh"
                            >
                                <RefreshIcon />
                            </IconButton>
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Jobs List */}
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {!selectedAccount ? (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#666'
                            }}>
                                Please select an account
                            </Box>
                        ) : loading && jobs.length === 0 ? (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <ImportJobsList
                                jobs={jobs}
                                loading={loading}
                                onCancelJob={handleCancelJob}
                                policySetMap={policySetMap}
                            />
                        )}
                    </Box>
                </Box>
                </Box>
            </Drawer>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default ImportJobsPanel;
